import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { ArrowLeft, Bike, Camera, Upload, X, Check, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import BottomNav from '@/components/BottomNav';

// Constants
const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB
const MAX_WIDTH = 1280;
const JPEG_QUALITY = 0.7;

// Compress image before upload
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Scale down if larger than max width
        if (width > MAX_WIDTH) {
          height = Math.round((height * MAX_WIDTH) / width);
          width = MAX_WIDTH;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(compressedFile);
            } else {
              reject(new Error('Compression failed'));
            }
          },
          'image/jpeg',
          JPEG_QUALITY
        );
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });
};

const DeliveryPartnerRegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Form text state
  const [formData, setFormData] = useState({
    user_name: '',
    mobile_number: '',
    whatsapp_number: '',
    aadhar_number: ''
  });

  // File state - store File objects, not base64
  const [files, setFiles] = useState({
    aadhar_photo: null,
    pancard_photo: null,
    driving_license_photo: null,
    live_selfie: null,
    bike_bag_photo: null
  });

  // Preview URLs - using object URLs for memory efficiency
  const [previews, setPreviews] = useState({
    aadhar_photo: null,
    pancard_photo: null,
    driving_license_photo: null,
    live_selfie: null,
    bike_bag_photo: null
  });

  // Camera state
  const [activeCameraField, setActiveCameraField] = useState(null);
  const [cameraError, setCameraError] = useState({});
  const videoRefs = useRef({});

  // Cleanup object URLs on unmount or when previews change
  useEffect(() => {
    return () => {
      // Revoke all object URLs on unmount
      Object.values(previews).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  // Handle text input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle file selection with compression
  const handleFileSelect = useCallback(async (field, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, [field]: 'Please select an image file' }));
      return;
    }

    // Check initial size
    if (file.size > MAX_FILE_SIZE * 2) {
      setErrors(prev => ({ ...prev, [field]: `Image too large. Please select an image under ${MAX_FILE_SIZE * 2 / 1024 / 1024}MB` }));
      return;
    }

    try {
      // Compress image
      const compressedFile = await compressImage(file);
      
      // Check compressed size
      if (compressedFile.size > MAX_FILE_SIZE) {
        setErrors(prev => ({ ...prev, [field]: `Image still too large after compression. Max size: ${MAX_FILE_SIZE / 1024 / 1024}MB` }));
        return;
      }

      // Revoke old preview URL
      if (previews[field]) {
        URL.revokeObjectURL(previews[field]);
      }

      // Create new preview URL
      const previewUrl = URL.createObjectURL(compressedFile);

      // Update state
      setFiles(prev => ({ ...prev, [field]: compressedFile }));
      setPreviews(prev => ({ ...prev, [field]: previewUrl }));
      setErrors(prev => ({ ...prev, [field]: '' }));
    } catch (error) {
      console.error('Compression error:', error);
      setErrors(prev => ({ ...prev, [field]: 'Failed to process image. Please try another.' }));
    }
  }, [previews]);

  // Handle file input change
  const handleFileUpload = (field, event) => {
    const file = event.target.files[0];
    handleFileSelect(field, file);
    // Reset input so same file can be selected again
    event.target.value = '';
  };

  // Start camera
  const startCamera = async (field) => {
    setCameraError(prev => ({ ...prev, [field]: '' }));
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: field === 'live_selfie' ? 'user' : 'environment' } 
      });
      
      setActiveCameraField(field);
      
      setTimeout(() => {
        if (videoRefs.current[field]) {
          videoRefs.current[field].srcObject = stream;
        }
      }, 100);
      
    } catch (err) {
      console.error('Camera permission error:', err);
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError(prev => ({ 
          ...prev, 
          [field]: 'Camera access denied. Please allow camera permission and try again.' 
        }));
      } else if (err.name === 'NotFoundError') {
        setCameraError(prev => ({ 
          ...prev, 
          [field]: 'No camera found on this device.' 
        }));
      } else {
        setCameraError(prev => ({ 
          ...prev, 
          [field]: 'Could not access camera. Please check your device settings.' 
        }));
      }
    }
  };

  // Capture photo from camera
  const capturePhoto = async (field) => {
    const video = videoRefs.current[field];
    if (!video) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    
    if (field === 'live_selfie') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    
    ctx.drawImage(video, 0, 0);
    
    // Convert to blob and create File
    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], `${field}_capture.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
        await handleFileSelect(field, file);
      }
      stopCamera(field);
    }, 'image/jpeg', JPEG_QUALITY);
  };

  // Stop camera
  const stopCamera = (field) => {
    const video = videoRefs.current[field];
    if (video && video.srcObject) {
      video.srcObject.getTracks().forEach(track => track.stop());
      video.srcObject = null;
    }
    setActiveCameraField(null);
  };

  // Remove uploaded image
  const removeImage = (field) => {
    // Revoke preview URL
    if (previews[field]) {
      URL.revokeObjectURL(previews[field]);
    }
    setFiles(prev => ({ ...prev, [field]: null }));
    setPreviews(prev => ({ ...prev, [field]: null }));
  };

  // Reset form completely
  const resetForm = useCallback(() => {
    // Revoke all preview URLs
    Object.values(previews).forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    
    // Reset all state
    setFormData({
      user_name: '',
      mobile_number: '',
      whatsapp_number: '',
      aadhar_number: ''
    });
    setFiles({
      aadhar_photo: null,
      pancard_photo: null,
      driving_license_photo: null,
      live_selfie: null,
      bike_bag_photo: null
    });
    setPreviews({
      aadhar_photo: null,
      pancard_photo: null,
      driving_license_photo: null,
      live_selfie: null,
      bike_bag_photo: null
    });
    setErrors({});
    setUploadProgress(0);
  }, [previews]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.user_name || formData.user_name.trim().length < 2) {
      newErrors.user_name = 'Name is required (minimum 2 characters)';
    }

    const mobile = formData.mobile_number.replace(/\+91|\s/g, '');
    if (!mobile || !/^\d{10}$/.test(mobile)) {
      newErrors.mobile_number = 'Enter valid 10-digit mobile number';
    }

    const whatsapp = formData.whatsapp_number.replace(/\+91|\s/g, '');
    if (!whatsapp || !/^\d{10}$/.test(whatsapp)) {
      newErrors.whatsapp_number = 'Enter valid 10-digit WhatsApp number';
    }

    const aadhar = formData.aadhar_number.replace(/\s/g, '');
    if (!aadhar || !/^\d{12}$/.test(aadhar)) {
      newErrors.aadhar_number = 'Enter valid 12-digit Aadhar number';
    }

    if (!files.aadhar_photo) {
      newErrors.aadhar_photo = 'Aadhar Card photo is required';
    }
    if (!files.pancard_photo) {
      newErrors.pancard_photo = 'PAN Card photo is required';
    }
    if (!files.driving_license_photo) {
      newErrors.driving_license_photo = 'Driving License photo is required';
    }
    if (!files.live_selfie) {
      newErrors.live_selfie = 'Live selfie is required';
    }
    if (!files.bike_bag_photo) {
      newErrors.bike_bag_photo = 'Photo with bike & bag is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form with FormData
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    try {
      // Create FormData
      const formDataObj = new FormData();
      formDataObj.append('user_name', formData.user_name);
      formDataObj.append('mobile_number', formData.mobile_number);
      formDataObj.append('whatsapp_number', formData.whatsapp_number);
      formDataObj.append('aadhar_number', formData.aadhar_number);
      formDataObj.append('aadhar_photo', files.aadhar_photo);
      formDataObj.append('pancard_photo', files.pancard_photo);
      formDataObj.append('driving_license_photo', files.driving_license_photo);
      formDataObj.append('live_selfie', files.live_selfie);
      formDataObj.append('bike_bag_photo', files.bike_bag_photo);

      const response = await axios.post(`${API}/delivery-partner/apply`, formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percent);
        }
      });
      
      if (response.data.success) {
        // Reset form and clear memory
        resetForm();
        setSuccess(true);
        toast.success('Registration submitted successfully!');
      }
    } catch (error) {
      console.error('Submit error:', error);
      if (error.response?.data?.detail?.errors) {
        setErrors(error.response.data.detail.errors);
        toast.error('Please fix the errors and try again');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit registration');
      }
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Image upload component
  const ImageUpload = ({ field, label, allowBoth = false }) => {
    const inputRef = useRef(null);
    const hasImage = previews[field];
    const isCameraActive = activeCameraField === field;

    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700">{label} *</Label>
        
        {hasImage ? (
          <div className="relative">
            <img 
              src={previews[field]} 
              alt={label} 
              className="w-full h-40 object-cover rounded-lg border"
            />
            <button
              type="button"
              onClick={() => removeImage(field)}
              className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs flex items-center gap-1">
              <Check className="w-3 h-3" /> 
              {files[field] ? `${(files[field].size / 1024).toFixed(0)}KB` : 'Ready'}
            </div>
          </div>
        ) : isCameraActive ? (
          <div className="relative">
            <video 
              ref={el => videoRefs.current[field] = el}
              autoPlay 
              playsInline 
              muted
              className="w-full h-48 object-cover rounded-lg bg-black"
              style={{ transform: field === 'live_selfie' ? 'scaleX(-1)' : 'none' }}
            />
            <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-3">
              <button
                type="button"
                onClick={() => capturePhoto(field)}
                className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg border-4 border-green-500"
              >
                <div className="w-10 h-10 bg-green-600 rounded-full" />
              </button>
              <button
                type="button"
                onClick={() => stopCamera(field)}
                className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            {allowBoth && (
              <button
                type="button"
                onClick={() => startCamera(field)}
                className="flex-1 flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
              >
                <Camera className="w-8 h-8 text-gray-400" />
                <span className="text-sm text-gray-500">Camera</span>
              </button>
            )}
            
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload(field, e)}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="flex-1 flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
            >
              <Upload className="w-8 h-8 text-gray-400" />
              <span className="text-sm text-gray-500">Upload</span>
            </button>
          </div>
        )}
        
        {cameraError[field] && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800">{cameraError[field]}</p>
              <button
                type="button"
                onClick={() => startCamera(field)}
                className="text-sm text-amber-700 font-medium underline mt-1"
              >
                Try Again
              </button>
            </div>
          </div>
        )}
        
        {errors[field] && (
          <p className="text-sm text-red-500">{errors[field]}</p>
        )}
      </div>
    );
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 pb-20">
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/profile')} className="p-2 hover:bg-gray-100 rounded-full">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <h1 className="text-2xl font-bold">Registration Form</h1>
            </div>
          </div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Submitted!</h2>
            <p className="text-gray-600 mb-6">
              Your delivery partner application has been submitted successfully. 
              We will review your documents and get back to you soon.
            </p>
            <Button 
              onClick={() => navigate('/profile')} 
              className="bg-green-600 hover:bg-green-700 text-white px-8"
            >
              Back to Profile
            </Button>
          </div>
        </div>
        
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)} 
              className="p-2 hover:bg-gray-100 rounded-full"
              data-testid="back-button"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold">Registration Form</h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Bike className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Delivery Partner Registration</h2>
              <p className="text-sm text-gray-500">Fill your details to register as a delivery partner.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* User Name */}
            <div className="space-y-2">
              <Label htmlFor="user_name" className="text-sm font-medium text-gray-700">User Name *</Label>
              <Input
                id="user_name"
                type="text"
                placeholder="Enter your full name"
                value={formData.user_name}
                onChange={(e) => handleInputChange('user_name', e.target.value)}
                className={errors.user_name ? 'border-red-500' : ''}
              />
              {errors.user_name && <p className="text-sm text-red-500">{errors.user_name}</p>}
            </div>

            {/* Mobile Number */}
            <div className="space-y-2">
              <Label htmlFor="mobile_number" className="text-sm font-medium text-gray-700">Mobile Number *</Label>
              <Input
                id="mobile_number"
                type="tel"
                placeholder="Enter 10-digit mobile number"
                value={formData.mobile_number}
                onChange={(e) => handleInputChange('mobile_number', e.target.value.replace(/[^0-9+]/g, ''))}
                maxLength={13}
                className={errors.mobile_number ? 'border-red-500' : ''}
              />
              {errors.mobile_number && <p className="text-sm text-red-500">{errors.mobile_number}</p>}
            </div>

            {/* WhatsApp Number */}
            <div className="space-y-2">
              <Label htmlFor="whatsapp_number" className="text-sm font-medium text-gray-700">WhatsApp Number *</Label>
              <Input
                id="whatsapp_number"
                type="tel"
                placeholder="Enter 10-digit WhatsApp number"
                value={formData.whatsapp_number}
                onChange={(e) => handleInputChange('whatsapp_number', e.target.value.replace(/[^0-9+]/g, ''))}
                maxLength={13}
                className={errors.whatsapp_number ? 'border-red-500' : ''}
              />
              {errors.whatsapp_number && <p className="text-sm text-red-500">{errors.whatsapp_number}</p>}
            </div>

            {/* Aadhar Number */}
            <div className="space-y-2">
              <Label htmlFor="aadhar_number" className="text-sm font-medium text-gray-700">Aadhar Card Number *</Label>
              <Input
                id="aadhar_number"
                type="text"
                placeholder="Enter 12-digit Aadhar number"
                value={formData.aadhar_number}
                onChange={(e) => handleInputChange('aadhar_number', e.target.value.replace(/[^0-9]/g, ''))}
                maxLength={12}
                className={errors.aadhar_number ? 'border-red-500' : ''}
              />
              {errors.aadhar_number && <p className="text-sm text-red-500">{errors.aadhar_number}</p>}
            </div>

            {/* Document Uploads */}
            <div className="pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Upload Documents</h3>
              <p className="text-xs text-gray-500 mb-4">Images will be compressed to max 1280px width. Max file size: 3MB each.</p>
              
              <div className="space-y-4">
                <ImageUpload 
                  field="aadhar_photo" 
                  label="Aadhar Card Photo"
                />
                
                <ImageUpload 
                  field="pancard_photo" 
                  label="PAN Card Photo"
                />
                
                <ImageUpload 
                  field="driving_license_photo" 
                  label="Driving License Photo"
                />
              </div>
            </div>

            {/* Live Photos */}
            <div className="pt-4 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Live Photos</h3>
              
              <div className="space-y-4">
                <div>
                  <ImageUpload 
                    field="live_selfie" 
                    label="Live Selfie"
                    allowBoth={true}
                  />
                  <p className="text-xs text-gray-500 mt-1">Submit live selfie for fast approval</p>
                </div>
                
                <ImageUpload 
                  field="bike_bag_photo" 
                  label="Photo with Bike & Delivery Bag"
                  allowBoth={true}
                />
              </div>
            </div>

            {/* Upload Progress */}
            {loading && uploadProgress > 0 && (
              <div className="pt-4">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Uploading documents...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {uploadProgress > 0 ? `Uploading ${uploadProgress}%...` : 'Submitting...'}
                  </>
                ) : (
                  'Submit Registration'
                )}
              </Button>
              <p className="text-gray-500 text-xs mt-2">Having trouble submitting? Contact us on WhatsApp: 7620715987</p>
            </div>
          </form>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default DeliveryPartnerRegisterPage;
